// Social share buttons for the Fêtes 2027 programme page.
// No third-party JS, no tracking — just intent URLs.
import { useState } from "react";

const SHARE_URL = (typeof window !== "undefined" ? window.location.origin : "") + "/presence#programme";

const TEXT = {
  fr: {
    title: "Partager cet événement",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    whatsapp: "WhatsApp",
    email: "Email",
    copy: "Copier le lien",
    copied: "Lien copié !",
    text: "Fêtes de saint Léonard — Millénaire 2027 · 2, 3 & 4 juillet 2027 à Vains.",
  },
  en: {
    title: "Share this event",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    whatsapp: "WhatsApp",
    email: "Email",
    copy: "Copy link",
    copied: "Link copied!",
    text: "Saint Leonard Feasts — Millennium 2027 · July 2-4, 2027 in Vains, Normandy.",
  },
};

export default function ShareButtons({ locale = "fr" }) {
  const t = TEXT[locale] || TEXT.fr;
  const [copied, setCopied] = useState(false);

  const text = encodeURIComponent(t.text);
  const url = encodeURIComponent(SHARE_URL);

  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
    whatsapp: `https://wa.me/?text=${text}%20${url}`,
    email: `mailto:?subject=${encodeURIComponent(t.text)}&body=${text}%0A%0A${url}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: select the text in a temp input
      const ta = document.createElement("textarea");
      ta.value = SHARE_URL;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div className="mt-6 flex flex-col items-center gap-3" data-testid="share-buttons">
      <span className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.22em" }}>
        ◇ {t.title} ◇
      </span>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <ShareIconLink href={links.facebook} label={t.facebook} testId="share-facebook" bg="#1877f2" path="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.52 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.9h-2.33v7.03C18.34 21.21 22 17.06 22 12.06z" />
        <ShareIconLink href={links.twitter} label={t.twitter} testId="share-twitter" bg="#0f1419" path="M18.244 2H21.5l-7.5 8.57L23 22h-6.83l-5.34-6.99L4.7 22H1.44l8.03-9.17L1 2h7l4.82 6.39L18.244 2zm-2.39 18h1.86L7.27 4H5.32l10.534 16z" />
        <ShareIconLink href={links.whatsapp} label={t.whatsapp} testId="share-whatsapp" bg="#25d366" path="M20.52 3.48A11.95 11.95 0 0 0 12.04 0C5.4 0 .03 5.37.03 12c0 2.12.55 4.18 1.6 6L0 24l6.2-1.62A11.94 11.94 0 0 0 12.04 24C18.67 24 24 18.63 24 12c0-3.21-1.25-6.22-3.48-8.52zM12.04 21.82c-1.85 0-3.66-.5-5.23-1.43l-.37-.22-3.69.96.98-3.6-.24-.37A9.78 9.78 0 0 1 2.22 12c0-5.4 4.4-9.8 9.82-9.8 2.62 0 5.08 1.02 6.93 2.87a9.72 9.72 0 0 1 2.87 6.94c0 5.4-4.4 9.8-9.8 9.8zm5.39-7.34c-.29-.15-1.74-.86-2.01-.96-.27-.1-.47-.15-.67.15-.2.29-.77.96-.94 1.16-.17.2-.35.22-.64.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.35.44-.52.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.91-2.19-.24-.57-.48-.49-.66-.5h-.57c-.2 0-.52.07-.79.37-.27.29-1.04 1.02-1.04 2.48 0 1.47 1.07 2.88 1.21 3.08.15.2 2.1 3.2 5.09 4.49.71.31 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.08 1.74-.71 1.99-1.4.24-.69.24-1.27.17-1.4-.07-.13-.27-.2-.56-.35z" />
        <ShareIconLink href={links.email} label={t.email} testId="share-email" bg="#7a6f60" path="M12 12.713l-11.99-7.713h23.98l-11.99 7.713zm0 2.574l-12-7.72v15.433l8.117-9.054 3.883 2.45 3.883-2.45 8.117 9.054v-15.433l-12 7.72zm-12 9.713l8.293-9.249 3.707 2.341 3.707-2.341 8.293 9.249h-24z" />
        <button onClick={copyLink} data-testid="share-copy"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-sm font-caps text-[0.6rem]"
                style={{ letterSpacing: "0.15em", border: "1px solid var(--border)",
                          backgroundColor: copied ? "var(--moss)" : "transparent",
                          color: copied ? "white" : "var(--ink)" }}>
          {copied ? `✓ ${t.copied}` : `⎘ ${t.copy}`}
        </button>
      </div>
    </div>
  );
}

function ShareIconLink({ href, label, testId, bg, path }) {
  return (
    <a href={href} target="_blank" rel="noreferrer noopener"
       data-testid={testId}
       title={label}
       aria-label={label}
       className="inline-flex items-center justify-center transition-transform hover:scale-110"
       style={{ width: 38, height: 38, borderRadius: 4, backgroundColor: bg }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
        <path d={path} />
      </svg>
    </a>
  );
}
